import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useDeleteResource } from '@app/src/api/updateHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import Table from '@app/src/components/Table'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import { getResponseExportColumnsAccessor } from '@app/src/export-columns/response'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSignalRListener from '@app/src/hooks/signalRListener'
import useSort from '@app/src/hooks/sorting'
import { useSelectableRowsWithPostSelectAll } from '@app/src/hooks/useSelectableRows/useSelectableRowsWithPostSelectAll'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { ResponseScraping } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { ADMIN_ROLES, NotificationSeverity, ResourceTypes, WfRequestTags } from '@app/src/wf-constants'
import { DeleteOutlineOutlined } from '@mui/icons-material'
import { useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useConfirm } from 'material-ui-confirm'
import React from 'react'
import { useIntl } from 'react-intl'
import { useDebouncedCallback } from 'use-debounce'
import ResponsesScrapingFilters from '../../../Filters/ResponsesScrapingFilters'
import type { FilterGroup } from '../../../Filters/useFilters'
import { Operators } from '../../../Filters/useFilters'
import AccessorScrapedResponseHeader from '../../Response/AccessorScrapedResponseHeader'
import AccessorScrapedResponseRow from '../../Response/AccessorScrapedResponseRow'

interface AccessorScrapedResponseSceneProps {
  relatedContentFilter?: FilterGroup[]
  embedded?: boolean
  tabs?: ResourceCollectionSceneProps['tabs']
  actionButtons?: ResourceCollectionSceneProps['actionButtons']
}

const SCRAPING_REFETCH_DEBOUNCE_MS = 2000
const SCRAPING_REFETCH_MAX_WAIT_MS = 5000

const allowedFilters = [
  'questionnaireTemplate.id',
  'periodName',
  'response.request.submittedAt',
  'response.request.internalVerificationStatus',
  'provider.countryId',
  'provider.id',
  'provider.priority',
  'provider.finalRiskRating',
  'provider.activityStatus',
  'provider.providerApprovalStatus',
  'provider.supplierUsage',
  'provider.tier',
  'provider.providerOwners.userId',
  'provider.providerOwners.user',
  'provider.spends.spendClassification',
  'provider.spends.periodName',
  'provider.spend',
  'provider.assessments.assessmentTemplate.name',
  'provider.assessments.assessmentTemplate.id',
  'provider.assessments.totalLevel',
  'provider.assessments.section1Level',
  'provider.assessments.section2Level',
  'provider.assessments.section3Level',
  'provider.assessments.section4Level',
  'provider.assessments.section5Level',
  'provider.baseline',
  'provider.customCategories',
  'externalVerificationStatus',
  'provider.organization.countryId',
  'startedAt',
  'response.internalVerificationStatus',
  'response.verifications.internalVerificationStatus',
  'provider.categoryOptions.id',
  'percentageFound',
  'status',
  'assessmentName',
  'totalLevel',
  'section1Level',
  'section2Level',
  'section3Level',
  'section4Level',
  'section5Level',
  'provider.mappingNodes.actorType.id',
  'provider.mappingNodes.tier',
  'provider.mappingNodes.product.productStatus',
]

const AccessorScrapedResponseScene: React.FC<AccessorScrapedResponseSceneProps> = ({
  embedded,
  tabs,
  actionButtons = [],
  relatedContentFilter = [],
}) => {
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  const { openDrawer } = useDrawer()
  const { role, solution } = useAuthentication().scope
  const isAdmin = ADMIN_ROLES.includes(role?.toLowerCase() ?? '')
  const { mutateAsync: deleteResponses, isPending: isDeletingResponses } = useDeleteResource()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const confirm = useConfirm()
  const apiFilters = useGetApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery(undefined, [apiFilters])
  const { showAdditionalExportColumns } = useFlags()

  const basePayload = {
    filter: relatedContentFilter,
    sort: sorting,
    include: [
      'questionnaireTemplate.image',
      'provider.organization',
      'response',
      'response.request.inquiries',
      'provider.country.name',
      'provider.referralContact',
      'provider.assessments.assessmentTemplate',
      'requestAutomation',
    ],
  }

  if (apiFilters.some(filter => filter.name.endsWith('Level'))) {
    const baselineAssessmentFilter: FilterGroup = {
      name: 'questionnaireTemplate.tags.tag',
      filters: [
        {
          value: WfRequestTags.PublicRequest3,
          operator: Operators.EqualTo,
        },
      ],
    }
    basePayload.filter.push(baselineAssessmentFilter)
  }

  const payload = {
    ...basePayload,
    filter: [...basePayload.filter, ...apiFilters],
    pagination: {
      pageNumber: page,
      itemsPerPage: pageSize,
    },
  }

  const {
    items: scrapedResponses,
    count,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useFetchCollectionWithPost<ResponseScraping>({
    key: [FetchKey.ResponseScraping],
    endpoint: endpoints.responseScraping,
    payload,
  })
  const refetchScrapedResponses = useDebouncedCallback(() => refetch(), SCRAPING_REFETCH_DEBOUNCE_MS, {
    maxWait: SCRAPING_REFETCH_MAX_WAIT_MS,
  })
  useSignalRListener('AiScrapingCompleted', refetchScrapedResponses)
  useSignalRListener('AiScrapingBatchCompleted', refetchScrapedResponses)

  const onDeleted = {
    onSuccess: () => {
      showSnackbar({
        message: formatMessage({ id: 'notifications.successfulResourceDelete' }),
        severity: NotificationSeverity.success,
      })
      setSelectedRowsIds([])
      queryClient.resetQueries({ queryKey: [FetchKey.ResponseScraping] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.Request] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.Answer] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseItemCount] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.Notification] })
      // Wait for the assessment to be calculated
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseScraping] })
      }, 2000)
    },
  }

  const confirmDeleteSelected = () => {
    confirm({
      description: formatMessage({ id: 'resourceCollections.delete.requests.dialogScrapedDescription' }),
      buttonOrder: ['confirm', 'cancel'],
      confirmationText: (
        <>
          <DeleteOutlineOutlined />
          {formatMessage({ id: 'general.delete' })}
        </>
      ),
      confirmationButtonProps: { style: { backgroundColor: 'white', color: 'black' } },
      cancellationButtonProps: { style: { backgroundColor: 'black', color: 'white' } },
    }).then(async () => {
      await deleteResponses({ url: endpoints.deleteScrapedResponsesByIds(selectedRowsIds) }, onDeleted)
    })
  }

  const {
    selectedRowsIds,
    setSelectedRowsIds,
    isLoadingAllRows,
    isAllSelected,
    handleSelectAll,
    handleCheckboxChange,
    isHeaderChecked,
  } = useSelectableRowsWithPostSelectAll<ResponseScraping>({
    rowsInPage: scrapedResponses,
    basePayload: payload,
    fetchKey: FetchKey.ResponseScraping,
    endpoint: endpoints.responseScraping,
    count,
  })

  const rawExportPayload = {
    ...payload,
    include: [
      'response',
      'response.creatorOrganization',
      'response.request.responderOrganization',
      'response.request.template',
      'response.request.template.image',
      'response.request.subscriptions',
      'provider.country.name',
      'response.request.inquiries',
      'response.request.target.organization',
      'response.items',
      'response.items.requestItem',
      'response.request.periodName',
    ],
  }

  const handleExport = (selectedIds?: number[]) => {
    openDrawer(
      <DrawerViewExport
        resourceType={ResourceTypes.ResponseScraping}
        resourceView={tabs?.activeTabParam}
        count={count}
        userFilter={basePayload.filter}
        exportColumns={getResponseExportColumnsAccessor(solution, true, undefined, showAdditionalExportColumns)}
        rawExportPayload={rawExportPayload}
        onlyLatest
        selectedIds={selectedIds}
      />,
    )
  }

  return (
    <ResourceCollectionScene
      title={formatMessage({ id: 'navbar.manageRequests' })}
      tabs={tabs}
      actionButtons={[
        {
          label: formatMessage({ id: 'resourceCollections.general.export' }),
          variant: 'outlined',
          onClick: () => handleExport(selectedRowsIds),
        },
        ...actionButtons,
      ]}
      filter={
        <ResponsesScrapingFilters
          implicitFilters={[...relatedContentFilter]}
          allowedFilters={allowedFilters}
          resetPage={resetPage}
        />
      }
      embedded={embedded}
      buttonRow={
        selectedRowsIds.length
          ? [
              ...insertIf(isAdmin, {
                label: formatMessage(
                  { id: 'resourceCollections.delete.requests.deleteCount' },
                  { count: selectedRowsIds?.length ?? 0 },
                ),
                startIcon: <DeleteOutlineOutlined />,
                onClick: confirmDeleteSelected,
                disabled: isLoading || isFetching || isLoadingAllRows || isDeletingResponses,
              }),
              {
                label: formatMessage(
                  { id: 'resourceCollections.general.exportWithCount' },
                  { count: selectedRowsIds.length },
                ),
                onClick: () => handleExport(selectedRowsIds),
              },
              ...insertIf<ActionButton>(count > pageSize, {
                label: formatMessage({ id: 'resourceCollections.general.selectAllRequests' }, { count }),
                variant: 'text',
                onClick: handleSelectAll,
                disabled: isLoading || isFetching || isAllSelected() || isDeletingResponses,
                loading: isLoadingAllRows,
              }),
            ]
          : undefined
      }
    >
      <Table<ResponseScraping>
        RowComponent={({ row }) => (
          <AccessorScrapedResponseRow
            row={row}
            onCheckboxChange={handleCheckboxChange}
            selectedResponsesIds={selectedRowsIds}
          />
        )}
        HeaderComponent={() => (
          <AccessorScrapedResponseHeader
            toggleSorting={toggleSorting}
            activeSorting={sorting}
            isHeaderChecked={isHeaderChecked}
            responsesInPage={scrapedResponses}
            setSelectedResponsesIds={setSelectedRowsIds}
          />
        )}
        data={scrapedResponses}
        isLoading={isLoading}
        count={count}
        isError={isError}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        stickyColumnIndex={2}
      />
    </ResourceCollectionScene>
  )
}

export default AccessorScrapedResponseScene
